import { API_BASE_URL } from '../config/serverApiConfig';
import errorHandler from '../request/errorHandler';
import successHandler from '../request/successHandler';
import { api } from './api';

export const createWorkspace = async (name: string, description: string, parentId?: number) => {
  try {
    const url = `${API_BASE_URL}r/v2/workspaces`;
    const response = await api().post(url, {
      name,
      description,
      ...(parentId ? { parent_id: parentId } : {})
    });
    successHandler(
      { response, status: response.status },
      {
        notifyOnSuccess: true,
        notifyOnFailed: true
      }
    );
    return response.data;
  } catch (error: any) {
    return errorHandler(error);
  }
};

// Get Workspace list by user
export const getWorkspaceOnboarding = async () => {
  const response = await api().get(`${API_BASE_URL}r/v2/workspaces/onboarding`);
  return response.data;
};

export const getWorkspaceList = async (q?: string) => {
  try {
    const url = q
      ? `${API_BASE_URL}r/v2/workspaces?q=${encodeURIComponent(q)}`
      : `${API_BASE_URL}r/v2/workspaces`;
    const response = await api().get(url);
    successHandler(
      { response, status: response.status },
      {
        notifyOnSuccess: false,
        notifyOnFailed: true
      }
    );
    // New API returns an array directly (not { workspaces: [] })
    return Array.isArray(response.data) ? response.data : (response.data?.workspaces ?? []);
  } catch (error: any) {
    return errorHandler(error);
  }
};

// `POST workspace/active` is gone and has no successor: there is no server-side notion of
// an "active" workspace to set. Switching is entirely client-side — the picker writes
// `default_workspace` to localStorage and reloads, and `api()` sends it as `x-workspace-id`
// on every request from then on. `setDefaultWorkspace` below is the separate, deliberate
// act of choosing which workspace you land in next time you log in.

export interface DeleteWorkspaceResult {
  success: boolean;
  status?: number;
  message?: string;
}

// Hard, irreversible delete: cascades to the workspace's assets, vulnerabilities,
// connectors (with their stored credentials), policies and memberships.
export const deleteWorkspace = async (workspaceId: string): Promise<DeleteWorkspaceResult> => {
  try {
    const url = `${API_BASE_URL}r/v2/workspaces/${workspaceId}`;
    const response = await api().delete(url);
    successHandler(
      { response, status: response.status },
      {
        notifyOnSuccess: true,
        notifyOnFailed: true
      }
    );
    return {
      success: true,
      status: response.status,
      message: response.data?.message
    };
  } catch (error: any) {
    const status = error?.response?.status;

    // A 404 is "not there, or not yours" (§4.1) — never a confirmed delete. The caller
    // reconciles it against a fresh workspace list and says what that showed, so routing
    // it through errorHandler as well would put a second, vaguer notification on screen
    // for the same event.
    if (status === 404) {
      return {
        success: false,
        status
      };
    }

    // errorHandler already surfaces the backend `detail` — which distinguishes
    // "not a member" from "member without permission" on a 403 — in a notification.
    const handled = errorHandler(error);
    return {
      success: false,
      status,
      message: handled?.message
    };
  }
};

export const setDefaultWorkspace = async (workspaceId: string) => {
  try {
    const url = `${API_BASE_URL}r/v2/workspaces/${workspaceId}/default`;
    const response = await api().put(url);
    successHandler(
      { response, status: response.status },
      { notifyOnSuccess: true, notifyOnFailed: true }
    );
    return response.data;
  } catch (error: any) {
    return errorHandler(error);
  }
};

export default {
  createWorkspace,
  getWorkspaceList,
  deleteWorkspace,
  setDefaultWorkspace
};
